import { useCallback, useState } from 'react';
import type {
  CreatePropertyRequest,
  GetPropertyResponse,
  PropertyWithSensorGroups,
  UpdatePropertyRequest,
} from '@metron/shared';
import { getApiUrl } from '../config/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { toErrorMessage } from '@metron/shared';

interface ListPropertiesParams {
  customerId: number;
  page?: number;
  limit?: number;
  search?: string;
}

interface ListPropertiesResponse {
  properties: PropertyWithSensorGroups[];
  total: number;
}

export function useProperties() {
  const { getAccessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * List properties for a customer with pagination and search
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const listProperties = useCallback(async ({
    customerId,
    page = 1,
    limit = 1000,
    search,
  }: ListPropertiesParams): Promise<ListPropertiesResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      // Build query string
      const params = new URLSearchParams({
        customerId: customerId.toString(),
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) {
        params.append('search', search);
      }

      const response = await fetch(getApiUrl(`properties?${params.toString()}`), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch properties');
      }

      const data: ListPropertiesResponse = await response.json();
      return data;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to fetch properties');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Get single property details with sensor groups
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const getProperty = useCallback(async (id: number): Promise<PropertyWithSensorGroups> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl(`properties/${id}`), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch property');
      }

      const data: GetPropertyResponse = await response.json();
      return data.property;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to fetch property');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Create a new property
   * Returns PropertyWithSensorGroups (with empty sensorGroups array)
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const createProperty = useCallback(
    async (data: CreatePropertyRequest): Promise<PropertyWithSensorGroups> => {
      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error('No access token available');
        }

        const response = await fetch(getApiUrl('properties'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error?.message || 'Failed to create property');
        }

        const responseData: { property: PropertyWithSensorGroups } = await response.json();
        return responseData.property;
      } catch (err) {
        const errorMessage = toErrorMessage(err, 'Failed to create property');
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken],
  );

  /**
   * Update property name, metron ID, and sensor group relationships
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const updateProperty = useCallback(async (
    id: number,
    data: UpdatePropertyRequest & {
      masterSensorGroupId?: number;
      childSensorGroupId?: number;
    },
  ): Promise<PropertyWithSensorGroups> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl(`properties/${id}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update property');
      }

      const responseData: GetPropertyResponse = await response.json();
      return responseData.property;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to update property');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Delete property
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const deleteProperty = useCallback(async (id: number): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl(`properties/${id}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to delete property');
      }
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to delete property');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Assign sensor group to property
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const assignSensorGroup = useCallback(async (
    propertyId: number,
    sensorGroupId: number,
  ): Promise<PropertyWithSensorGroups> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl(`properties/${propertyId}/sensor-groups/assign`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sensorGroupId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to assign sensor group');
      }

      const responseData: GetPropertyResponse = await response.json();
      return responseData.property;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to assign sensor group');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Unassign sensor groups from property
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const unassignSensorGroups = useCallback(async (
    propertyId: number,
    sensorGroupIds: number[],
  ): Promise<PropertyWithSensorGroups> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl(`properties/${propertyId}/sensor-groups/assign`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sensorGroupIds }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to unassign sensor groups');
      }

      const responseData: GetPropertyResponse = await response.json();
      return responseData.property;
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to unassign sensor groups');
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Add master-child relationship between sensor groups
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const addMasterChildRelationship = useCallback(async (
    propertyId: number,
    masterSensorGroupId: number,
    childSensorGroupId: number,
  ): Promise<PropertyWithSensorGroups> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        getApiUrl(`properties/${propertyId}/sensor-groups/add-master`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ masterSensorGroupId, childSensorGroupId }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to add master-child relationship');
      }

      // After adding relationship, fetch the updated property with all sensor groups
      const updatedProperty = await getProperty(propertyId);
      return updatedProperty;
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to add master-child relationship';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken, getProperty]);

  /**
   * Remove master-child relationship (breaks the relationship)
   * Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
   */
  const removeMasterChildRelationship = useCallback(async (
    propertyId: number,
    masterSensorGroupId: number,
  ): Promise<PropertyWithSensorGroups> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(
        getApiUrl(`properties/${propertyId}/sensor-groups/remove-master`),
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ masterSensorGroupId }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || 'Failed to remove master-child relationship',
        );
      }

      // After removing relationship, fetch the updated property with all sensor groups
      const updatedProperty = await getProperty(propertyId);
      return updatedProperty;
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to remove master-child relationship';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken, getProperty]);

  return {
    listProperties,
    getProperty,
    createProperty,
    updateProperty,
    deleteProperty,
    assignSensorGroup,
    unassignSensorGroups,
    addMasterChildRelationship,
    removeMasterChildRelationship,
    isLoading,
    error,
  };
}
